/**
 * Marker orchestration facade.
 *
 * Why this file exists:
 * - Keeps the public marker lifecycle API in one place (create/sync/clear/destroy).
 * - Coordinates specialized modules so map features do not need to know implementation details.
 * - Preserves behavior hooks (`setMarkerBehavior`) while keeping rendering and indexing concerns isolated.
 *
 * Responsibilities:
 * - Own marker state and lifecycle.
 * - Bridge map camera events to visibility recalculation.
 * - Delegate work to spatial index, pool, renderer, visibility resolver, and viewport controller.
 */
import createSpatialIndex from './spatialIndex';
import createMarkerPool from './markerPool';
import createMarkerRenderer from './markerRenderer';
import createViewportController from './viewportController';
import { computeVisibleMarkers, resolveDefaultGroupingKey } from './markerVisibility';
import { expandBounds } from './utils';
import { createCameraSnapshot, isCameraSnapshotEqual } from './mapSnapshot';
import { shouldShowCommunityISMIndicator } from '../../features/searchResultsMap/services/markersBehavior';

const BOUNDS_PADDING = 0.03;
const MAX_POOL_SIZE = 600;

/**
 * Marker facade wrapper used by map features.
 *
 * Key extension points:
 * - setMarkerBehavior: feature code can decide marker mode/visibility at runtime.
 * - syncMarkers: preferred bulk update path for result changes.
 * - onPoolExhausted: optional callback fired when a marker is dropped because the
 *   pool hit MAX_POOL_SIZE. Use this to emit a metric, log a warning, or surface
 *   the capacity pressure to an observability layer.
 */
export default function markers(facade, adapterState, { onPoolExhausted } = {}) {
  const state = {
    markerLibrary: null,
    markersById: new Map(),
    resolveMarkerMode: null,
    resolveMarkerVisibility: null,
    onClick: null,
    visibleMarkerIds: new Set(),
  };

  let lastCameraSnapshot = null;
  let lastAppliedModeZoom = -1;
  let syncGeneration = 0;
  let runVisibilityUpdate = () => {};

  function getMap() {
    return adapterState?.map || null;
  }

  function getMarkerLibrary() {
    if (state.markerLibrary) {
      return state.markerLibrary;
    }

    state.markerLibrary = adapterState?.google?.maps?.marker || null;
    return state.markerLibrary;
  }

  const spatialIndex = createSpatialIndex();
  const renderer = createMarkerRenderer();

  const markerPool = createMarkerPool({
    getMarkerLibrary,
    createMarkerElement: renderer.createMarkerElement,
    maxPoolSize: MAX_POOL_SIZE,
    onRecordClick: (record, event) => {
      state.onClick?.(record.markerData, event);
    },
    resetRecord: renderer.resetRecord,
  });

  const viewportController = createViewportController({
    onMapEvent: facade.onMapEvent,
    onUpdate: () => runVisibilityUpdate(),
  });

  function invalidateVisibilitySnapshot() {
    lastCameraSnapshot = null;
    // Force mode re-application on the next idle frame even if zoom is unchanged.
    // This covers syncMarkers (new records with mode=null) and setMarkerBehavior
    // (behavior change may affect all resolved modes).
    lastAppliedModeZoom = -1;
  }

  function syncInteractiveSiteMapElement(record, markerData) {
    const map = getMap();
    const zoom = map?.getZoom?.() ?? null;
    const shouldShowInteractiveSiteMap = shouldShowCommunityISMIndicator(markerData, zoom);
    record.element.classList.toggle(
      'nhs-c-marker--has-interactive-site-map',
      shouldShowInteractiveSiteMap
    );
  }

  function bindMarkerRecord(record, markerData) {
    renderer.bindRecord(record, markerData);
    syncInteractiveSiteMapElement(record, markerData);
    spatialIndex.updateRecord(record, markerData.position);
  }

  function releaseMarkerRecord(record) {
    spatialIndex.removeRecord(record);
    markerPool.release(record);
  }

  function buildVisibilityCandidates(paddedBounds) {
    const candidateIds = spatialIndex.getViewportMarkerIds(paddedBounds);
    const candidateRecords = [];

    candidateIds.forEach((id) => {
      const record = state.markersById.get(id);
      if (!record) {
        return;
      }

      if (record.markerData.ignoreVisibilityUpdates) {
        if (record.marker.map != null) {
          candidateRecords.push(record);
        }
        return;
      }

      candidateRecords.push(record);
    });

    return candidateRecords;
  }

  function flushVisibilityWrites(toShowIds, toHideIds, map) {
    // Show pass — guard: id must still be a winner in case a subsequent
    // updateMarkersVisibility ran between the rAF and this task.
    toShowIds.forEach((id) => {
      if (state.visibleMarkerIds.has(id)) {
        const record = state.markersById.get(id);
        if (record && record.marker.map !== map) {
          renderer.setMarkerVisible(record, map, true);
        }
      }
    });

    // Hide pass — guard: id must still be a loser, and must still be on map.
    toHideIds.forEach((id) => {
      if (!state.visibleMarkerIds.has(id)) {
        const record = state.markersById.get(id);
        if (record && record.marker.map != null) {
          renderer.setMarkerVisible(record, map, false);
        }
      }
    });
  }

  function applyVisibilityDiff(winnerIds, map) {
    // Collect what needs to change before committing the new visible set.
    const toShowIds = [];
    winnerIds.forEach((id) => {
      const record = state.markersById.get(id);
      if (record && record.marker.map !== map) {
        toShowIds.push(id);
      }
    });

    const toHideIds = [];
    state.visibleMarkerIds.forEach((id) => {
      if (!winnerIds.has(id)) {
        toHideIds.push(id);
      }
    });

    // Commit the new visible set synchronously so any re-entrant call or
    // guard check in the deferred task sees up-to-date state.
    state.visibleMarkerIds = winnerIds;

    if (toShowIds.length === 0 && toHideIds.length === 0) {
      return;
    }

    // Defer ALL marker.map writes to a task after paint.
    setTimeout(() => flushVisibilityWrites(toShowIds, toHideIds, map), 0);
  }

  function applyMarkerModes(winnerIds, zoom, isInteracting, paddedBounds) {
    const behaviorContext = {
      zoom,
      isInteracting,
      bounds: paddedBounds,
    };

    winnerIds.forEach((id) => {
      const record = state.markersById.get(id);

      if (!record || !renderer.canApplyMode(record)) {
        return;
      }

      const mode = renderer.resolveMode(
        record.markerData,
        behaviorContext,
        state.resolveMarkerMode
      );

      syncInteractiveSiteMapElement(record, record.markerData);
      renderer.applyMode(record, mode);
    });
  }

  function updateMarkersVisibility() {
    const map = getMap();
    if (!map) {
      return;
    }

    const isInteracting = viewportController.getIsInteracting();
    const currentSnapshot = createCameraSnapshot(map, isInteracting);
    if (isCameraSnapshotEqual(lastCameraSnapshot, currentSnapshot)) {
      return;
    }

    lastCameraSnapshot = currentSnapshot;
    if (isInteracting) {
      return;
    }

    const zoom = currentSnapshot.zoom;
    const literalBounds = currentSnapshot.boundsNorth != null ? {
      north: currentSnapshot.boundsNorth,
      south: currentSnapshot.boundsSouth,
      east: currentSnapshot.boundsEast,
      west: currentSnapshot.boundsWest,
    } : null;
    if (!literalBounds) {
      return;
    }

    const paddedBounds = expandBounds(literalBounds, BOUNDS_PADDING);
    const candidateRecords = buildVisibilityCandidates(paddedBounds);
    const winnerIds = computeVisibleMarkers({
      markers: candidateRecords,
      bounds: paddedBounds,
      zoom,
      behavior: {
        isInteracting,
        resolveMarkerVisibility: state.resolveMarkerVisibility,
        resolveGroupingKey: resolveDefaultGroupingKey,
      },
    });

    // Mode (pin vs price) is solely a function of zoom level.
    // When zoom changes, re-apply modes to all winners.
    // When zoom is unchanged (e.g. a pan), only apply modes to newly visible
    // markers — their record.mode is null because they were hidden in the
    // previous frame and the pool reset their state on release. Already-visible
    // markers retain valid cached modes and need no update.
    if (zoom !== lastAppliedModeZoom) {
      applyMarkerModes(winnerIds, zoom, isInteracting, paddedBounds);
      lastAppliedModeZoom = zoom;
    } else {
      const newWinnerIds = new Set();
      winnerIds.forEach((id) => {
        if (!state.visibleMarkerIds.has(id)) {
          newWinnerIds.add(id);
        }
      });
      applyMarkerModes(newWinnerIds, zoom, isInteracting, paddedBounds);
    }

    applyVisibilityDiff(winnerIds, map);
  }

  runVisibilityUpdate = updateMarkersVisibility;

  function scheduleVisibilityUpdate() {
    viewportController.scheduleUpdate();
  }

  /**
   * Registers optional per-marker behavior callbacks.
   *
   * resolveMode(markerData, context) => 'pin' | 'price' | custom
   * resolveVisibility(markerData, context) => boolean | undefined
   * onClick(markerData, event) => void
   */
  function setMarkerBehavior({ resolveMode, resolveVisibility, onClick } = {}) {
    state.resolveMarkerMode = typeof resolveMode === 'function' ? resolveMode : null;
    state.resolveMarkerVisibility = typeof resolveVisibility === 'function' ? resolveVisibility : null;
    state.onClick = typeof onClick === 'function' ? onClick : null;
    invalidateVisibilitySnapshot();
    scheduleVisibilityUpdate();
  }

  function clearMarkerBehavior() {
    state.resolveMarkerMode = null;
    state.resolveMarkerVisibility = null;
    state.onClick = null;
    invalidateVisibilitySnapshot();
    scheduleVisibilityUpdate();
  }

  function upsertMarkerRecord(markerData) {
    if (!markerData.position || markerData.id == null) {
      return null;
    }

    if (state.markersById.has(markerData.id)) {
      const existing = state.markersById.get(markerData.id);
      bindMarkerRecord(existing, markerData);
      return existing;
    }

    const record = markerPool.acquire();
    if (!record) {
      onPoolExhausted?.(markerData);
      return null;
    }

    bindMarkerRecord(record, markerData);
    state.markersById.set(markerData.id, record);

    return record;
  }

  function createMarker(markerData) {
    const record = upsertMarkerRecord(markerData);

    if (!record) {
      return null;
    }

    invalidateVisibilitySnapshot();
    scheduleVisibilityUpdate();

    return record.marker;
  }

  function clearMarkers() {
    state.markersById.forEach((record) => {
      releaseMarkerRecord(record);
    });

    state.markersById.clear();
    state.visibleMarkerIds.clear();
    spatialIndex.clear();
    invalidateVisibilitySnapshot();
  }

  /**
   * Bulk-replaces the current marker set with markersToSync.
   *
   * Markers with invalid data (missing position or id) are silently skipped —
   * this is intentional for bulk operations where partial results are acceptable.
   *
   * Markers that cannot be acquired from the pool (capacity limit reached) are
   * also silently skipped within this operation. To observe pool exhaustion,
   * pass an `onPoolExhausted` callback to the `markers()` factory.
   *
   * Processing is chunked across tasks to avoid a single long task blocking the
   * main thread. A generation counter ensures that if a newer syncMarkers call
   * arrives while chunks are in flight, the superseded run is abandoned.
   */
  function syncMarkers(markersToSync = []) {
 const CHUNK_SIZE = 50;
    syncGeneration += 1;
    const generation = syncGeneration;
    const nextIds = new Set();
    let index = 0;

    function processNextChunk() {
      if (generation !== syncGeneration) {
        return;
      }

      const end = Math.min(index + CHUNK_SIZE, markersToSync.length);

      while (index < end) {
        const markerData = markersToSync[index];
        index += 1;
        const record = upsertMarkerRecord(markerData);
        if (record) {
          nextIds.add(markerData.id);
        }
      }

      if (index < markersToSync.length) {
        setTimeout(processNextChunk, 0);
        return;
      }

      // All chunks processed — remove stale markers.
      const staleIds = [];

      state.markersById.forEach((_, id) => {
        if (!nextIds.has(id)) {
          staleIds.push(id);
        }
      });

      staleIds.forEach((id) => {
        const record = state.markersById.get(id);
        releaseMarkerRecord(record);
        state.markersById.delete(id);
      });

      invalidateVisibilitySnapshot();

      if (!viewportController.getIsInteracting()) {
        scheduleVisibilityUpdate();
      }
    }

    processNextChunk();
  }

  function destroyMarkers() {
    viewportController.cancelScheduledUpdate();

    clearMarkers();

    markerPool.drain();
    viewportController.destroy();

    state.resolveMarkerMode = null;
    state.resolveMarkerVisibility = null;
    lastCameraSnapshot = null;
    lastAppliedModeZoom = -1;
  }

  function removeMarker(id) {
    const record = state.markersById.get(id);
    if (!record) {
      return;
    }

    releaseMarkerRecord(record);
    state.markersById.delete(id);
    state.visibleMarkerIds.delete(id);
    invalidateVisibilitySnapshot();
  }

  /**
   * Creates a raw AdvancedMarkerElement that lives entirely outside the
   * pool, spatial index, and visibility system. The caller is responsible
   * for disposing it by setting marker.map = null.
   *
   * Use this for short-lived, purely visual markers that must not interfere
   * with the managed marker layer (e.g. transient hover highlights).
   */
  function createUnmanagedMarker(markerData) {
    const markerLibrary = getMarkerLibrary();
    if (!markerLibrary) {
      return null;
    }

    const map = getMap();
    if (!map) {
      return null;
    }

    const element = renderer.createMarkerElement(markerData);
    syncInteractiveSiteMapElement({ element }, markerData);
    
    element.dataset.id = markerData.id ?? '';
    element.dataset.communityId = String(markerData.communityId ?? '');
    element.dataset.isBasic = String(markerData.isBasic ?? false);
    element.dataset.type = String(markerData.type ?? '');

    return new markerLibrary.AdvancedMarkerElement({
      map,
      position: markerData.position,
      title: markerData.title ?? '',
      zIndex: markerData.zIndex,
      content: element,
    });
  }

  function destroyMap() {
    destroyMarkers();
    return facade.destroyMap?.();
  }

  return {
    ...facade,
    createMarker,
    createUnmanagedMarker,
    removeMarker,
    syncMarkers,
    updateMarkersVisibility,
    setMarkerBehavior,
    clearMarkerBehavior,
    destroyMarkers,
    destroyMap,
  };
}